import { z } from "zod";

/**
 * M2 — human-pairing rate over the TRAILING 7 DAYS, one point per day.
 *
 * This is the metric the tab exists for: the share of pairable matches that
 * found a HUMAN opponent. It is not a fill rate — that is
 * `matchmaking/queue-health`, and the two answer different questions. Both
 * figures here are proposal counts over the clean universe (onboarding,
 * directed rematches and all-bot house matches excluded).
 *
 * The day-alone and trailing-7-day figures are BOTH returned, deliberately.
 * The rolling window's failure mode is a scan that opens at `from` instead of
 * `from − 6 days`, which under-counts the first six points in silence. Carrying
 * both makes the day and its trailing window visible side by side instead of
 * leaving the panel with nothing to check itself against.
 *
 * Daily grain only. The trailing window is fixed at 7 days, and an hourly grain
 * would fan each anchor onto 168 buckets instead of 7 — measured at 372 ms and
 * a 1,403-block temp spill over a 100-day range, against a 200 ms gate.
 */
export const ArenaOpsMatchmakingPairingRateSeriesResponseSchema = z.object({
  points: z.array(
    z.object({
      /** UTC midnight of the day this trailing window ENDS on. */
      bucketStart: z.iso.datetime(),
      /** Clean-universe proposals on this day ALONE. */
      cleanDay: z.number().int(),
      /** Of `cleanDay`, the ones that paired two humans. */
      hvhDay: z.number().int(),
      /** Clean-universe proposals over the trailing 7 days — the rate's denominator. */
      clean7d: z.number().int(),
      /** Of `clean7d`, the ones that paired two humans — the rate's numerator. */
      hvh7d: z.number().int(),
      /**
       * `hvh7d ÷ clean7d`, a RATIO in `[0, 1]` at 4 dp — never a percentage.
       * Formatting is the frontend's job; a wire format that is sometimes one
       * and sometimes the other is how a chart ends up 100x off. `oracle.sql`
       * prints this column as a percentage, so a harness comparing the two
       * must scale by 100.
       *
       * Empty string when the trailing window holds no pairable match: no rate
       * exists, and `"0.0000"` would assert one. Test `clean7d === 0`.
       */
      hvhRate7d: z.string(),
    }),
  ),
});
export type ArenaOpsMatchmakingPairingRateSeriesResponse = z.infer<
  typeof ArenaOpsMatchmakingPairingRateSeriesResponseSchema
>;
